/**
 * Booking URL constants and utilities
 */

export const DEFAULT_BOOKING_URL = "https://bookings.gettimely.com/raintreechinesemedicine/bb/book"
export const NOOSA_CLINIC_BOOKING_URL = "https://bookings.gettimely.com/raintreechinesemedicine/bb/book?location=367410&staff=633975"
export const MAROOCHYDORE_CLINIC_BOOKING_URL = "https://bookings.gettimely.com/raintreechinesemedicine/bb/book?location=382401&staff=633975"
export const FREE_CONSULT_BOOKING_URL = "https://bookings.gettimely.com/raintreechinesemedicine/bb/book?location=382409&category=655265&product=4970489%3ASV&staff=633975"

/**
 * Determines if a booking source string indicates a "Book Noosa Clinic" button click
 * Checks if the source contains "noosa" (case-insensitive) to catch all variations
 * @param source - The booking source string from analytics tracking
 * @returns true if the source indicates a Noosa Clinic booking
 */
export function isNoosaClinicBooking(source?: string): boolean {
  if (!source) return false
  const sourceLower = source.toLowerCase()
  // Check if source contains "noosa" (case-insensitive) to catch all variations
  // This includes: noosa_clinic, tewantin_clinic, continue_care_noosa, noosa_hero,
  // noosa_location, about_noosa_clinic, noosa_fees_*, homepage_locations_noosa,
  // locations_section_noosa, continue_care_locations_noosa, how_to_book_noosa, etc.
  return sourceLower.includes('noosa') || sourceLower === 'tewantin_clinic'
}

/**
 * Determines if a booking source string indicates a "Book Maroochydore Clinic" button click
 * Checks if the source contains "maroochydore" (case-insensitive) to catch all variations
 * @param source - The booking source string from analytics tracking
 * @returns true if the source indicates a Maroochydore Clinic booking
 */
export function isMaroochydoreClinicBooking(source?: string): boolean {
  if (!source) return false
  const sourceLower = source.toLowerCase()
  // Check if source contains "maroochydore" (case-insensitive) to catch all variations
  // This includes: maroochydore_clinic, maroochydore_hero, maroochydore_location,
  // maroochydore_fees_*, homepage_locations_maroochydore, locations_section_maroochydore,
  // continue_care_locations_maroochydore, how_to_book_maroochydore, etc.
  return sourceLower.includes('maroochydore')
}

/**
 * Determines if a booking source string indicates a "Free 15-min Phone Consult" button click
 * Checks if the source contains "free_consult" (case-insensitive) to catch all variations
 * @param source - The booking source string from analytics tracking
 * @returns true if the source indicates a Free Consult booking
 */
export function isFreeConsultBooking(source?: string): boolean {
  if (!source) return false
  const sourceLower = source.toLowerCase()
  // Check if source contains "free_consult" (case-insensitive) to catch all variations
  // This includes: free_consult_header, continue_care_free_consult, other_hero_free_consult,
  // fertility_cta_free_consult, how_to_book_free_consult, dr_renee_free_consult, etc.
  return sourceLower.includes('free_consult')
}

/**
 * Returns the appropriate booking URL based on the source string
 * Priority: Free Consult > Noosa > Maroochydore > Default
 * @param source - The booking source string from analytics tracking
 * @returns The booking URL for Free Consult, Noosa, Maroochydore, or default
 */
export function getBookingUrl(source?: string): string | undefined {
  // Check free consult first (highest priority)
  if (isFreeConsultBooking(source)) {
    return FREE_CONSULT_BOOKING_URL
  }
  // Then check clinic-specific bookings
  if (isNoosaClinicBooking(source)) {
    return NOOSA_CLINIC_BOOKING_URL
  }
  if (isMaroochydoreClinicBooking(source)) {
    return MAROOCHYDORE_CLINIC_BOOKING_URL
  }
  return undefined // Will use default URL
}

/**
 * Extracts the service pillar ID from a booking source string
 * Used to pre-select a pillar when booking from a pillar-specific page
 * @param source - The booking source string from analytics tracking
 * @returns The pillar ID if detected, null otherwise
 * 
 * Pillar IDs match those defined in booking-appointments.ts:
 * - fertility, pregnancy, menstrual, stress-sleep, womens-health, other
 */
export function getPillarFromSource(source?: string): string | null {
  if (!source) return null
  const s = source.toLowerCase()
  
  // Check for pillar-specific sources (e.g., "fertility_hero_initial_consult")
  // Only match if it's an "initial_consult" type - these should pre-select the pillar
  if (s.includes('fertility') && s.includes('initial')) return 'fertility'
  if (s.includes('pregnancy') && s.includes('initial')) return 'pregnancy'
  if (s.includes('menstrual') && s.includes('initial')) return 'menstrual'
  if (s.includes('stress') && s.includes('initial')) return 'stress-sleep'
  if (s.includes('longterm') && s.includes('initial')) return 'womens-health'
  if (s.includes('other') && s.includes('initial') && !s.includes('maroochydore')) return 'other'
  
  return null
}

/**
 * Determines if a booking source string indicates an Extended Appointment booking
 * Extended appointments are only available for General Health
 * @param source - The booking source string from analytics tracking
 * @returns true if the source indicates an Extended Appointment booking
 */
export function isExtendedAppointmentBooking(source?: string): boolean {
  if (!source) return false
  const s = source.toLowerCase()
  return s.includes('extended')
}

/**
 * Determines if a booking source string indicates a General Health Initial Appointment booking
 * This is specifically for homepage Initial Consultation cards that should pre-select General Health + Initial
 * @param source - The booking source string from analytics tracking
 * @returns true if the source indicates a General Health Initial Appointment booking
 */
export function isGeneralHealthInitialBooking(source?: string): boolean {
  if (!source) return false
  const s = source.toLowerCase()
  // Match homepage_initial_appointment specifically (not pillar-specific initial sources)
  return s.includes('homepage_initial_appointment') || s === 'homepage_initial'
}

/**
 * Determines if a booking source string indicates a Noosa Return Appointment booking
 * This pre-selects Noosa + General Health + Return, going directly to summary
 * @param source - The booking source string from analytics tracking
 * @returns true if the source indicates a Noosa Return Appointment booking
 */
export function isNoosaReturnAppointmentBooking(source?: string): boolean {
  if (!source) return false
  const s = source.toLowerCase()
  return s.includes('noosa') && s.includes('return') && s.includes('appointment')
}

/**
 * Determines if a booking source string indicates a Maroochydore Return Appointment booking
 * This pre-selects Maroochydore + General Health + Return, going directly to summary
 * @param source - The booking source string from analytics tracking
 * @returns true if the source indicates a Maroochydore Return Appointment booking
 */
export function isMaroochydoreReturnAppointmentBooking(source?: string): boolean {
  if (!source) return false
  const s = source.toLowerCase()
  return s.includes('maroochydore') && s.includes('return') && s.includes('appointment')
}

